import React, { FC, Fragment, CSSProperties, useState } from 'react';
import { Box, Button, Typography, List, ListItem, useTheme, Divider } from '@mui/material';
import { Category, SubCategory } from 'models/organization';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { saveSubCategoryTemplate, deleteSubCategoryTemplate } from 'apollo/templates/action-sets';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import AddSubCategoryDialog from './add-sub-category-dialog.component';

type SubCategorySectionProps = {
    categories: Category[];
    actionSetId: string;
    reFetchActionSet?: (actionSetId: string) => void;
};

const SubCategorySection: FC<SubCategorySectionProps> = ({ categories, actionSetId, reFetchActionSet }) => {
    const theme = useTheme();
    const { t: translate } = useTranslation(['client-directory', 'common']);
    const [subCategory, setSubCategory] = useState<SubCategory>({} as SubCategory);
    const [addSubCategoryDialogOpen, setAddSubCategoryDialogOpen] = useState(false);
    const { showBoundary } = useDenthubErrorBoundary();

    const [saveSubCategory] = useMutation(saveSubCategoryTemplate, {
        onCompleted: () => {
            if (reFetchActionSet) {
                reFetchActionSet(actionSetId);
            }
            closeAddSubCategoryDialog();
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const [deleteSubCategory] = useMutation(deleteSubCategoryTemplate, {
        onCompleted: () => {
            if (reFetchActionSet) {
                reFetchActionSet(actionSetId);
            }
            closeAddSubCategoryDialog();
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const getButtonColor = (subCategory: SubCategory): CSSProperties => ({
        backgroundColor: subCategory.colorCode || '#ffffff',
        color: subCategory.textColorCode || '#000000',
    });

    const onAddEditSubCategory = (subCategory?: SubCategory) => {
        setSubCategory(subCategory || ({ colorCode: '#ffffff', textColorCode: '#000000' } as SubCategory));
        setAddSubCategoryDialogOpen(true);
    };

    const closeAddSubCategoryDialog = () => {
        setSubCategory({ colorCode: '#ffffff', textColorCode: '#000000' } as SubCategory);
        setAddSubCategoryDialogOpen(false);
    };

    const onSaveSubCategory = (subCategory: SubCategory) => {
        saveSubCategory({
            variables: {
                _id: subCategory._id,
                name: subCategory.name,
                parentCategoryId: subCategory.parentCategoryId,
                colorCode: subCategory.colorCode,
                textColorCode: subCategory.textColorCode,
            },
        });
    };

    const onDeleteSubCategory = (parentCategoryId: string, subCategoryId?: string) => {
        deleteSubCategory({
            variables: {
                categoryId: parentCategoryId,
                subCategoryId: subCategoryId,
            },
        });
    };

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                {translate('client-directory:SUB_CATEGORY')}
            </Typography>
            <Divider />
            <Button
                onClick={() => onAddEditSubCategory()}
                sx={{
                    mt: 2,
                    mb: 2,
                    height: 'fit-content',
                    minWidth: 150,
                    p: 1.5,
                    display: 'flex',
                }}
            >
                + {translate('common:ADD')}
            </Button>

            {categories?.map((category, i) => (
                <Box
                    key={i}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 2,
                        flexWrap: 'wrap',
                        gap: 2,
                    }}
                >
                    {category.subCategories?.length > 0 && (
                        <Fragment>
                            <Typography variant="subtitle1" sx={{ minWidth: '150px', fontWeight: 'bold' }}>
                                {i + 1}. {category.name}:
                            </Typography>

                            <List
                                disablePadding
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 1,
                                    p: 0,
                                    m: 0,
                                }}
                            >
                                {category.subCategories.map((subCategory, j) => (
                                    <ListItem
                                        key={j}
                                        onClick={() => onAddEditSubCategory(subCategory)}
                                        sx={{
                                            ...getButtonColor(subCategory),
                                            width: 'auto',
                                            borderRadius: 1,
                                            px: 2,
                                            py: 1,
                                            cursor: 'pointer',
                                            transition: '0.3s',
                                            '&:hover': {
                                                backgroundColor: theme.palette.action.hover,
                                            },
                                        }}
                                    >
                                        {subCategory.name}
                                    </ListItem>
                                ))}
                            </List>
                        </Fragment>
                    )}
                </Box>
            ))}
            <AddSubCategoryDialog
                open={addSubCategoryDialogOpen}
                onClose={closeAddSubCategoryDialog}
                selectedSubCategory={subCategory as SubCategory}
                categories={categories}
                onSave={onSaveSubCategory}
                onDelete={onDeleteSubCategory}
            />
        </Box>
    );
};

export default SubCategorySection;
